import React, { useEffect, useState } from 'react';
import { Building2, ChevronRight, CreditCard, HelpCircle, LogOut, Network, Settings, ShieldCheck, Trash2, Users } from 'lucide-react';
import type { AppUser, PlanUsageDTO } from '../../global';
import type { ViewMode } from '../../types';
import { Avatar } from '../../components/ui/Avatar';
import { planName } from '../preferences/plans';
import { dateLocale, t } from '../../lib/i18n';
import { MobileCard, MobileRow, MobileTitle } from './MobileUI';

interface MobileMoreViewProps {
  user: AppUser | null;
  isAdmin: boolean;
  onNavigate: (view: ViewMode) => void;
  /** Profil kartı → Genel ayarlar. */
  onOpenProfile: () => void;
  onOpenSettings: () => void;
  onOpenSubscription: () => void;
  onOpenHelp: () => void;
  onSignOut: () => void;
}

/** "13 sa 20 dk", "20 sa", "45 dk". */
function formatMinutes(total: number): string {
  const minutes = Math.max(0, Math.round(total));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return t(`${m} dk`, `${m} min`);
  if (m === 0) return t(`${h} sa`, `${h} h`);
  return t(`${h} sa ${m} dk`, `${h} h ${m} min`);
}

/** "Daha fazla" sekmesi: profil, bu dönemki kullanım ve ikincil ekranlar. */
export const MobileMoreView: React.FC<MobileMoreViewProps> = ({
  user,
  isAdmin,
  onNavigate,
  onOpenProfile,
  onOpenSettings,
  onOpenSubscription,
  onOpenHelp,
  onSignOut,
}) => {
  const [usage, setUsage] = useState<PlanUsageDTO | null>(null);

  useEffect(() => {
    let alive = true;
    window.zabit
      .getPlanUsage()
      .then((next) => alive && setUsage(next))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Biraz kullanım bile görünsün: yuvarlama sıfıra düşürmesin.
  const percent =
    usage && usage.quotaMinutes > 0
      ? Math.min(100, Math.max(usage.usedMinutes > 0 ? 2 : 0, Math.round((usage.usedMinutes / usage.quotaMinutes) * 100)))
      : 0;
  const nearlyFull = percent >= 90;

  return (
    <div className="px-5 pb-8 space-y-3.5 select-none">
      <MobileTitle title={t('Daha fazla', 'More')} />

      <button
        type="button"
        onClick={onOpenProfile}
        className="w-full flex items-center gap-3.5 p-4 rounded-[20px] border border-border bg-card text-left active:bg-secondary/60 transition-colors"
      >
        <Avatar src={user?.avatarDataUrl} name={user?.name || user?.email || '?'} className="size-[52px] text-[17px]" />
        <span className="flex-1 min-w-0 flex flex-col gap-0.5">
          <span className="text-[17px] font-semibold text-foreground truncate">{user?.name || user?.email}</span>
          <span className="text-[14px] text-muted-foreground line-clamp-2">{t('Profili ve ayarları düzenle', 'Edit profile and settings')}</span>
        </span>
        <ChevronRight className="w-[18px] h-[18px] shrink-0 text-muted-foreground" />
      </button>

      {/* Bu dönemki kullanım — profil kartı gibi bir kutu, içinde çubuk */}
      <button
        type="button"
        onClick={onOpenSubscription}
        className="w-full p-4 rounded-[20px] border border-border bg-card text-left space-y-3 active:bg-secondary/60 transition-colors"
      >
        <span className="flex items-center justify-between">
          <span className="text-[15px] font-semibold text-foreground">{t('Bu dönemki kullanım', 'Usage this period')}</span>
          <span className="text-[12px] font-bold px-2.5 py-1 rounded-full bg-brand-soft text-brand-text">
            {planName(usage?.plan ?? user?.plan ?? 'basic')}
          </span>
        </span>
        {usage ? (
          <>
            <span className="flex items-baseline gap-1.5">
              <span className="text-[26px] font-bold tracking-tight text-foreground">{formatMinutes(usage.usedMinutes)}</span>
              <span className="text-[15px] text-muted-foreground">/ {formatMinutes(usage.quotaMinutes)}</span>
            </span>
            <span
              role="progressbar"
              aria-label={t('Bu dönemki kullanım', 'Usage this period')}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
              className="block h-2 rounded-full bg-secondary overflow-hidden"
            >
              <span
                className={`block h-full rounded-full ${nearlyFull ? 'bg-record' : 'bg-brand'}`}
                style={{ width: `${percent}%` }}
              />
            </span>
            <span className="flex justify-between gap-3 text-[13.5px]">
              <span className={`font-semibold ${nearlyFull ? 'text-record-text' : 'text-foreground'}`}>
                {t(
                  `${formatMinutes(usage.quotaMinutes - usage.usedMinutes)} kaldı`,
                  `${formatMinutes(usage.quotaMinutes - usage.usedMinutes)} left`
                )}
              </span>
              <span className="text-muted-foreground">
                {t('Yenilenme', 'Renews')}:{' '}
                {new Date(usage.periodEndISO).toLocaleDateString(dateLocale(), { day: 'numeric', month: 'long' })}
              </span>
            </span>
          </>
        ) : (
          <span className="block h-2 rounded-full bg-secondary" />
        )}
      </button>

      <MobileCard>
        <MobileRow icon={<Users />} title={t('Kişiler', 'People')} onClick={() => onNavigate('people')} />
        <MobileRow icon={<Building2 />} title={t('Şirketler', 'Companies')} onClick={() => onNavigate('companies')} />
        <MobileRow icon={<Network />} title={t('Entegrasyonlar', 'Integrations')} onClick={() => onNavigate('integrations')} />
        <MobileRow icon={<Trash2 />} title={t('Çöp kutusu', 'Trash')} onClick={() => onNavigate('trash')} />
      </MobileCard>

      <MobileCard>
        <MobileRow icon={<Settings />} title={t('Ayarlar', 'Settings')} onClick={onOpenSettings} />
        <MobileRow icon={<CreditCard />} title={t('Abonelik', 'Subscription')} onClick={onOpenSubscription} />
        <MobileRow icon={<HelpCircle />} title={t('Yardım ve geri bildirim', 'Help and feedback')} onClick={onOpenHelp} />
        {isAdmin && <MobileRow icon={<ShieldCheck />} title={t('Yönetim', 'Admin')} onClick={() => onNavigate('settings')} />}
      </MobileCard>

      <MobileCard>
        <MobileRow icon={<LogOut />} title={t('Çıkış yap', 'Sign out')} tone="danger" chevron={false} onClick={onSignOut} />
      </MobileCard>
    </div>
  );
};
